<script setup lang="ts">
import { computed, ref } from 'vue'
import { NButton, NUpload } from 'naive-ui'
import type { UploadFileInfo } from 'naive-ui'
import { storeToRefs } from 'pinia'
import { useSourcesStore, partitionFiles, filesToRetry, ALLOWED_MIMES, MAX_FILE_BYTES } from '../stores/sources'

// The File tab of AddToWikiDrawer — owns the picked-file list, the
// client + backend rejection summary, and the retry-only-the-failures
// prune. Split out of the drawer so the parent stays under the 300-line
// limit once the redesign added labels and the explanatory header.
const sources = useSourcesStore()
const { submitting, submitError } = storeToRefs(sources)

const BYTES_PER_MB = 1_048_576
const maxMegabytes = MAX_FILE_BYTES / BYTES_PER_MB

const pickedFiles = ref<File[]>([])
const fileRejections = ref<Array<{ filename: string; reason: string }>>([])

const acceptMime = computed(() => ALLOWED_MIMES.join(','))
const canSubmitFile = computed(() => pickedFiles.value.length > 0 && !submitting.value)
const submitLabel = computed(() =>
  pickedFiles.value.length > 1 ? `Ingest ${pickedFiles.value.length} files` : 'Ingest file',
)

async function onSubmitFile() {
  if (!canSubmitFile.value) return
  const batch = pickedFiles.value.slice()
  const results = await sources.submitFiles(batch)
  const refusedByBackend = results
    .filter((r) => !r.ok)
    .map((r) => ({ filename: r.file.name, reason: r.error ?? 'submit failed' }))

  // Keep only files that failed at submit so the user can retry them
  // without re-dropping. `filesToRetry` matches on a stable name/size/
  // lastModified key rather than File-reference identity, so the prune
  // survives the store cloning the File. Append any new rejections
  // to the existing client-layer list so the summary stays one place.
  pickedFiles.value = filesToRetry(pickedFiles.value, results)
  if (refusedByBackend.length > 0) {
    fileRejections.value = [...fileRejections.value, ...refusedByBackend]
  }
}

// naive-ui's NUpload emits {fileList} with UploadFileInfo entries; we
// extract the raw File refs and hand them to `partitionFiles` so the
// validation rules stay owned by the store. One invalid file in a
// batched drop doesn't block the valid ones from submitting.
function onFileChange(payload: { fileList: UploadFileInfo[] }) {
  const raw = payload.fileList.map((info) => info.file as File | null | undefined).filter((f): f is File => !!f)
  const { accepted, rejected } = partitionFiles(raw)
  pickedFiles.value = accepted
  fileRejections.value = rejected
}

function clearRejections() {
  fileRejections.value = []
}
</script>

<template>
  <div class="flex flex-col gap-4 pt-4">
    <div class="flex flex-col gap-2">
      <span class="font-medium text-ink">Files to ingest</span>
      <NUpload
        :accept="acceptMime"
        multiple
        :default-upload="false"
        :show-file-list="true"
        data-testid="file-drop"
        @change="onFileChange"
      >
        <div
          class="rounded-xl border border-dashed border-border-control bg-surface-bg px-4 py-7 text-center text-sm leading-normal text-ink-muted"
        >
          Drop one or more <code class="font-mono text-ink">.pdf</code>, <code class="font-mono text-ink">.md</code>, or
          <code class="font-mono text-ink">.txt</code> files — or click to pick.
          <span class="mt-1 block text-xs text-ink-faint">Up to {{ maxMegabytes }} MB each.</span>
        </div>
      </NUpload>
    </div>

    <section
      v-if="fileRejections.length"
      class="flex flex-col gap-1 rounded-xl border border-status-alert/40 bg-status-alert/10 p-3 text-sm text-status-alert"
      data-testid="file-rejections"
    >
      <div class="flex items-center justify-between gap-2">
        <span>{{ fileRejections.length }} file{{ fileRejections.length === 1 ? '' : 's' }} couldn't be uploaded:</span>
        <NButton size="small" quaternary class="min-h-10" @click="clearRejections">Dismiss</NButton>
      </div>
      <ul class="m-0 list-disc pl-5">
        <li v-for="r in fileRejections" :key="r.filename">
          <span class="font-mono text-xs">{{ r.filename }}</span>
          — {{ r.reason }}
        </li>
      </ul>
    </section>

    <div class="flex flex-wrap items-center justify-end gap-3">
      <p v-if="submitError" class="m-0 mr-auto text-sm text-status-alert">{{ submitError }}</p>
      <NButton
        type="primary"
        class="min-h-11 px-5 font-semibold"
        :loading="submitting"
        :disabled="!canSubmitFile"
        data-testid="file-submit"
        @click="onSubmitFile"
      >
        {{ submitLabel }}
      </NButton>
    </div>
  </div>
</template>
